import { useState } from "react";
import urlUtils from "@/common/utils/urlUtils";
import { valueNames } from "@/modules/Constants";
import type ExperienceData from "@/modules/ExperienceData";
import * as watchTimeTracker from "@/modules/WatchTimeTracker";
import config from "@/modules/config";
import type { ResolutionSource } from "@/common/videojs/resolutionTypes";
import type { TimelineMarker } from "@/common/videojs/timelineMarkerTypes";
import type { ChainNavigation } from "@/components/player/useChainNavigation";
import type { VideoTracking } from "@/components/player/useVideoTracking";
import {
  type AutoplayAttempt,
  templateSizes,
  type VideoJsSelf,
  type VideoPlayer,
} from "@/components/player/videoJsTypes";
import type { ExperienceArticle, TimelineEntry } from "@/types/experience";

// Applies the experience to the player: source, poster, autoplay, markers, size.
function createPlayerSource(
  self: VideoJsSelf,
  chain: ChainNavigation,
  tracking: VideoTracking,
) {
  // Reads the player at call time; throws on a null player as the class did.
  const playerNow = () => self.player as VideoPlayer;

  function cancelAutoplayFallback() {
    if (self.currentAutoplayAttempt) {
      clearTimeout(self.currentAutoplayAttempt.timer);
      self.currentAutoplayAttempt = null;
    }
  }

  // Push the current orientation's template dimensions onto the (persistent)
  // player so its `.video-js` box matches the freshly-rendered layout. Landscape
  // forces `.video-js` to 100% via CSS `!important`, so this only visibly bites in
  // portrait, but syncing unconditionally keeps `player.width()/height()` honest.
  // Guarded on change so redundant re-renders don't thrash Video.js's layout.
  function syncPlayerDimensions() {
    if (!self.player || self.player.isDisposed()) {
      return;
    }
    try {
      const dims = templateSizes(self.props.experienceState).player.video;
      if (!dims || !dims.width || !dims.height) {
        return;
      }
      if (
        self.player.width() !== dims.width ||
        self.player.height() !== dims.height
      ) {
        self.player.dimensions(dims.width, dims.height);
        self.player.trigger("playerresize");
      }
    } catch {
      /* best-effort resize */
    }
  }

  function posterUrlForPlayer(experienceData: ExperienceData, loaded: boolean) {
    if (!loaded) {
      const embed = config.bannerSource();
      if (embed) {
        if (embed.indexOf("//") === 0) {
          return urlUtils.protocol() + embed;
        }
        return embed;
      }
    }
    return experienceData.getPosterImageUrl();
  }

  function updatePlayer() {
    if (!self.player) {
      return;
    }

    const experienceData = self.props.experienceState.experienceData;
    const applicationData = self.props.experienceState.applicationData;
    const loaded = experienceData.isLoaded();

    if (applicationData.pauseVideo() && !self.player.paused()) {
      self.player.pause();
    }

    // biome-ignore lint/suspicious/noDoubleEquals: the position comes from unchecked payload offsets; the loose comparison is the original one
    if (applicationData.videoPosition() != -1 && !self.isSeeking) {
      self.player.currentTime(applicationData.videoPosition());
    }

    if (experienceData.idexperience === undefined) {
      return;
    }

    const needsApply =
      experienceData.idexperience !== self.idexperience ||
      loaded !== self.appliedExperienceLoaded;

    if (!needsApply) {
      if (self.props.timelineName !== self.timelineName && loaded) {
        setupVideoMarkers();
      }
      tracking.reportMediaReady();
      self.timelineName = self.props.timelineName;
      return;
    }

    if (experienceData.idexperience !== self.idexperience) {
      self.mediaReadyExperienceId = null;
    }
    self.appliedExperienceLoaded = loaded;

    const posterImageUrl = posterUrlForPlayer(experienceData, loaded);
    const videoSrc = loaded ? experienceData.getDefaultVideoSrc() : "";
    let autoPlay = false;
    // A chain advance auto-starts the swapped-in video via the manual tryPlay()
    // below (see the play-on-load block). Read the flag here too — but only READ
    // (the play block clears it) — so the poster is hidden up front like the
    // normal autoplay path, instead of flashing until tryPlay renders.
    const chainAutoplay = applicationData.getValue(
      valueNames.CHAIN_AUTOPLAY,
      false,
    );

    if (videoSrc && videoSrc.src) {
      autoPlay =
        config.resolveAutoPlay(experienceData.getAutoPlay() === true) &&
        !self.pendingUserPlay;

      self.player.autoplay(!!autoPlay);
      // Muted autoplay is the only form modern browsers (esp. Safari/iOS) allow without a same-frame gesture.
      if (autoPlay) {
        try {
          self.player.muted(true);
        } catch {
          /* ignore */
        }
      }

      // Set up the resolution switcher in BOTH orientations. The quality cog is
      // populated (and un-hidden) only when `updateSrc(resolutions)` fires —
      // which lives inside `setupResolutionSwitcher()`. Gating it to landscape left
      // the portrait cog with zero menu items, so Video.js kept it `vjs-hidden` until
      // a rotate into landscape finally populated it. `setupResolutionSwitcher()`
      // already falls back to a plain single `src` when the experience has no
      // alternate video modes, so this is safe in portrait and equivalent to
      // `player.src(videoSrc)` for single-quality.
      setupResolutionSwitcher();
    } else if (!loaded) {
      self.player.autoplay(false);
      try {
        self.player.src([]);
      } catch {
        /* ignore */
      }
    }

    cancelAutoplayFallback();

    // Hide the poster whenever the video will start on its own — the
    // experience-level autoplay OR a chain advance (played via tryPlay below).
    // Muted setup stays gated on `autoPlay` above so a chain advance still
    // tries unmuted first (continuity), falling back to muted in tryPlay.
    const willAutoStart =
      autoPlay ||
      (chainAutoplay && !self.pendingUserPlay && videoSrc && videoSrc.src);

    if (willAutoStart) {
      self.player.addClass("vjs-pix-skip-poster");
      try {
        self.player.poster("");
      } catch {
        /* ignore */
      }
      // Fallback if autoplay is rejected (e.g. low-power mode): restore poster so the player isn't blank.
      const restorePoster = () => {
        if (self.currentAutoplayAttempt !== attempt) {
          return;
        }
        self.currentAutoplayAttempt = null;
        if (!self.player) {
          return;
        }
        self.player.removeClass("vjs-pix-skip-poster");
        if (posterImageUrl) {
          self.player.poster(posterImageUrl);
        }
      };
      // `attempt` is declared below; restorePoster runs 1.5 s later at the earliest.
      const attempt: AutoplayAttempt = {
        timer: setTimeout(restorePoster, 1500),
      };
      self.currentAutoplayAttempt = attempt;
      self.player.one("playing", () => {
        if (self.currentAutoplayAttempt !== attempt) {
          return;
        }
        clearTimeout(attempt.timer);
        self.currentAutoplayAttempt = null;
      });
      self.player.one("error", () => {
        if (self.currentAutoplayAttempt !== attempt) {
          return;
        }
        restorePoster();
      });
    } else {
      self.player.removeClass("vjs-pix-skip-poster");
      if (posterImageUrl) {
        self.player.poster(posterImageUrl);
      }
    }

    self.idexperience = experienceData.idexperience;
    // The current experience changed → refresh the prev/next arrows
    // (chainNav.recordCurrent already ran in the store on this load).
    chain.updateChainNavButtons();

    if (loaded) {
      setupVideoMarkers();
    } else {
      try {
        self.player.markers.set([]);
      } catch {
        /* ignore */
      }
    }

    self.percentComplete = 0;
    self.videoEndedFired = false;
    self.videoEndedPending = false;
    watchTimeTracker.reset();

    // A chain advance sets CHAIN_AUTOPLAY so the swapped-in video continues
    // playing without a fresh click (flag read above). Consume it only when we
    // actually play (i.e. once `loaded`) — clearing it on an earlier,
    // still-loading pass would eat it before the loaded pass runs.
    if (
      loaded &&
      (self.pendingUserPlay || chainAutoplay) &&
      videoSrc &&
      videoSrc.src
    ) {
      self.pendingUserPlay = false;
      if (chainAutoplay) {
        applicationData.setValue(valueNames.CHAIN_AUTOPLAY, false);
      }
      const player = self.player;
      const tryPlay = () => {
        (player.play() as Promise<void>).catch(() => {
          // An unmuted programmatic play on the freshly-swapped element can be
          // rejected by the autoplay policy; fall back to muted so the chained
          // video still plays (continuity beats silent-vs-sound).
          try {
            player.muted(true);
          } catch {
            /* ignore */
          }
          (player.play() as Promise<void>).catch(() => {
            /* ignore the rejection */
          });
        });
      };
      if (player.readyState() >= 2) {
        tryPlay();
      } else {
        player.one("loadeddata", tryPlay);
      }
    }

    tracking.reportMediaReady();
    self.timelineName = self.props.timelineName;
  }

  function setupVideoMarkers() {
    const experienceData = self.props.experienceState.experienceData;
    const timelineName = self.props.timelineName as string | undefined;
    const timeline = experienceData.getTimeline(timelineName, 0);
    const markers: TimelineMarker[] = [];
    for (let i = 0, max = timeline ? timeline.length : 0; i < max; i++) {
      const entry = (timeline as TimelineEntry[])[i];
      const article = experienceData.getArticle(
        timelineName,
        0,
        i,
      ) as ExperienceArticle;
      markers.push({
        time: entry.offset as number,
        text: article.title as string | undefined,
        id: entry.ids[0],
        index: i,
      });
    }
    playerNow().markers.set(markers);
  }

  function setupResolutionSwitcher() {
    const experienceData = self.props.experienceState.experienceData;
    const modes = experienceData.getVideoModes();
    const resolutions: ResolutionSource[] = [];
    const fallbackSrc = experienceData.getDefaultVideoSrc();

    if (!modes || !modes.length) {
      if (fallbackSrc && fallbackSrc.src) {
        playerNow().src(fallbackSrc);
      }
      return;
    }

    playerNow().defaultResolution(experienceData.getDefaultVideoMode());

    for (let i = 0; i < modes.length; i++) {
      const src = experienceData.getVideoSrc(modes[i]);
      if (src) {
        resolutions.push({
          src: src.src,
          type: src.type,
          label: modes[i],
          res: modes[i],
        });
      }
    }

    if (resolutions.length > 0) {
      playerNow().updateSrc(resolutions);
    } else if (fallbackSrc && fallbackSrc.src) {
      playerNow().src(fallbackSrc);
    }
  }

  return { cancelAutoplayFallback, syncPlayerDimensions, updatePlayer };
}

export type PlayerSource = ReturnType<typeof createPlayerSource>;

/** The source/poster/autoplay apply of one VideoJs mount; stable for the mount. */
export function usePlayerSource(
  self: VideoJsSelf,
  chain: ChainNavigation,
  tracking: VideoTracking,
): PlayerSource {
  const [source] = useState(() => createPlayerSource(self, chain, tracking));
  return source;
}
